import axios from 'axios';
import log from 'loglevel';

export interface ApiTransportOptions {
  baseURL: string;
  timeout: number;
}

export enum Level {
  error = 0,
  warn = 1,
  info = 2,
  debug = 4,
}

// Mapping Level enum to loglevel strings
export const levelToString: Record<Level, log.LogLevelDesc> = {
  [Level.error]: 'error',
  [Level.warn]: 'warn',
  [Level.info]: 'info',
  [Level.debug]: 'debug',
};

export class ApiTransport {
  private axiosInstance;

  constructor({ baseURL, timeout }: ApiTransportOptions) {
    this.axiosInstance = axios.create({
      baseURL,
      timeout,
    });
  }

  log(level: string, message: string, extra?: Record<string, unknown>) {
    this.axiosInstance
      .post('/log', {
        message,
        level,
        extra,
      })
      .catch(() => {
        // Silently ignore logging errors
      });
  }
}

export function setupApiLogging(apiTransport: ApiTransport): void {
  const originalFactory = log.methodFactory;
  log.methodFactory = function methodFactory(
    methodName: string,
    logLevel: any,
    loggerName: string | symbol,
  ) {
    const rawMethod = originalFactory(
      methodName as log.LogLevelNames,
      logLevel,
      loggerName,
    );
    return function logMethod(...args: unknown[]) {
      rawMethod(...args);

      let message = '';
      const extra: Record<string, unknown> = {
        timestamp: new Date().toISOString(),
      };

      for (const arg of args) {
        if (!message && typeof arg === 'string') {
          message = arg;
        } else if (typeof arg === 'object' && arg !== null) {
          // If the object has a 'message' property, append it to the message string
          if ('message' in arg && typeof (arg as any).message === 'string') {
            message = message
              ? `${message} ${(arg as any).message}`
              : (arg as any).message;
          }
          // Merge all other properties into extra (excluding 'message')
          Object.entries(arg).forEach(([key, value]) => {
            if (key !== 'message') {
              extra[key] = value;
            }
          });
        }
      }

      apiTransport.log(methodName, message, extra);
    };
  };
  log.setLevel(log.getLevel());
}
